<template>
  <div class="competition-list-container">
    <transition name="fade">
      <div
        v-if="showNotificationFlag"
        class="notification"
        :class="notificationType"
      >
        {{ notificationMessage }}
      </div>
    </transition>

    <!-- Header Section -->
    <div class="list-header">
      <h1 class="list-title">{{ t('competitions.registrationCompetitionsPageTitle') }}</h1>
      <button type="button" @click="showCreateModal = true" class="btn btn-primary">
        {{ t('competitions.createCompetition') }}
      </button>
    </div>

    <!-- Competitions List -->
    <div v-if="competitions.length === 0" class="empty-state">
      <h3>{{ t('competitions.noCompetitions') }}</h3>
      <p>{{ t('competitions.noCompetitionsDesc') }}</p>
      <button @click="showCreateModal = true" class="btn btn-primary">
        {{ t('competitions.createFirstCompetition') }}
      </button>
    </div>

    <div v-else class="competitions-grid">
      <table class="competitions-table">
        <thead>
          <tr>
            <th>{{ t('competitions.competitionName') }}</th>
            <th>{{ t('competitions.description') }}</th>
            <th>{{ t('general.status') }}</th>
            <th>{{ t('competitions.registrationForm') }}</th>
            <th>{{ t('competitions.maxParticipants') }}</th>
            <th class="text-right">
              {{ t('competitions.registrationDeadline') }}
            </th>
            <th class="text-right">{{ t('general.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="competition in competitions"
            :key="competition.id"
            :class="{ inactive: competition.status === 'inactive' }"
          >
            <td class="competition-title-cell">
              {{ competition.name }}
            </td>
            <td class="competition-description-cell truncate">
              {{ competition.description || '-' }}
            </td>
            <td>
              <span class="status-badge" :class="getStatusClass(competition)">
                {{ getStatusText(competition) }}
              </span>
            </td>
            <td class="meta-value">
              {{ getFormName(competition.registration_form_id) }}
            </td>
            <td class="meta-value">
              {{ competition.max_participants || t('general.unlimited') }}
            </td>
            <td class="date-cell text-right">
              {{ formatDate(competition.registration_deadline) }}
            </td>
            <td>
              <div class="competition-actions">
                <button
                  type="button"
                  @click="viewSubmissions(competition)"
                  class="btn btn-action"
                >
                  {{ t('competitions.viewSubmissions') }} ({{ submissionCount(competition) }})
                </button>
                <button
                  type="button"
                  @click="editCompetition(competition)"
                  class="btn btn-action"
                >
                  {{ t('general.edit') }}
                </button>
                <button
                  type="button"
                  @click="deleteCompetition(competition.id)"
                  class="btn btn-action-danger"
                >
                  {{ t('general.delete') }}
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Create/Edit Competition Modal -->
    <transition name="fade">
      <div
        v-if="showCreateModal || showEditModal"
        class="modal-overlay"
        @click="closeModal"
      >
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>
              {{
                editingCompetition
                  ? t('competitions.editCompetition')
                  : t('competitions.createCompetition')
              }}
            </h3>
            <button @click="closeModal" class="close-btn">&times;</button>
          </div>

          <form @submit.prevent="saveCompetition" class="modal-body">
            <div class="form-group">
              <label for="comp-name"
                >{{ t('competitions.competitionName') }} *</label
              >
              <input
                id="comp-name"
                v-model="competitionForm.name"
                type="text"
                required
                :placeholder="t('competitions.competitionNamePlaceholder')"
              />
            </div>

            <div class="form-group">
              <label for="comp-desc">{{ t('competitions.description') }}</label>
              <textarea
                id="comp-desc"
                v-model="competitionForm.description"
                rows="3"
                :placeholder="t('competitions.descriptionPlaceholder')"
              ></textarea>
            </div>

            <div class="form-group">
              <label for="comp-status">{{ t('competitions.status') }}</label>
              <select id="comp-status" v-model="competitionForm.status">
                <option value="draft">
                  {{ t('competitions.statusDraft') }}
                </option>
                <option value="active">
                  {{ t('competitions.statusActive') }}
                </option>
                <option value="completed">
                  {{ t('competitions.statusCompleted') }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label for="reg-form"
                >{{ t('competitions.registrationForm') }} *</label
              >
              <select
                id="reg-form"
                v-model="competitionForm.registration_form_id"
                required
              >
                <option value="">
                  {{ t('competitions.selectRegistrationForm') }}
                </option>
                <option
                  v-for="form in registrationForms"
                  :key="form.id"
                  :value="form.id"
                >
                  {{ form.title || form.name }}
                </option>
              </select>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label for="max-participants">{{
                  t('competitions.maxParticipants')
                }}</label>
                <input
                  id="max-participants"
                  v-model.number="competitionForm.max_participants"
                  type="number"
                  min="1"
                  :placeholder="t('competitions.maxParticipantsPlaceholder')"
                />
              </div>

              <div class="form-group">
                <label for="reg-deadline">{{
                  t('competitions.registrationDeadline')
                }}</label>
                <input
                  id="reg-deadline"
                  v-model="competitionForm.registration_deadline"
                  type="date"
                />
              </div>
            </div>

            <div class="modal-footer">
              <button type="submit" class="btn modal-save-btn">
                {{
                  editingCompetition
                    ? t('button.save')
                    : t('competitions.createCompetition')
                }}
              </button>
              <button
                type="button"
                @click="closeModal"
                class="btn modal-cancel-btn"
              >
                {{ t('button.cancel') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>

    <!-- Submissions Modal -->
    <SubmissionsList
      :is-visible="showSubmissionsModal"
      :competition="selectedCompetition"
      @close="closeSubmissionsModal"
      @deleted-count="onSubmissionsDeleted"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { useNotification } from '@/composables/useNotification'
import { useI18n } from 'vue-i18n'
import SubmissionsList from './SubmissionsList.vue'

export default {
  name: 'CompetitionList',
  components: {
    SubmissionsList,
  },
  setup() {
    // Reactive state
    const competitions = ref([])
    const registrationForms = ref([])
    const showCreateModal = ref(false)
    const showEditModal = ref(false)
    const showSubmissionsModal = ref(false)
    const editingCompetition = ref(false)
    const selectedCompetition = ref(null)

    const { t } = useI18n()
    const {
      showNotificationFlag,
      notificationMessage,
      notificationType,
      showNotification,
    } = useNotification()

    // Form state
    const competitionForm = ref({
      name: '',
      description: '',
      status: 'draft',
      registration_form_id: '',
      max_participants: null,
      registration_deadline: '',
    })

    // Computed properties
    const getFormName = computed(() => (formId) => {
      if (!formId) return t('competitions.noFormAssigned')
      const form = registrationForms.value.find((f) => f.id === formId)
      return form ? form.title || form.name : t('competitions.formNotFound')
    })

    // Methods
    const fetchCompetitions = async () => {
      try {
        // This would need a new API endpoint for competitions with registration data
        const response = await axios.get('/registration/competitions')
        competitions.value = response.data
      } catch (error) {
        console.error('Error fetching competitions:', error)
        showNotification('Failed to fetch competitions', 'error')
      }
    }

    const fetchRegistrationForms = async () => {
      try {
        const response = await axios.get('/registration/forms')
        registrationForms.value = response.data
      } catch (error) {
        console.error('Error fetching registration forms:', error)
        showNotification('Failed to fetch registration forms', 'error')
      }
    }

    const resetForm = () => {
      competitionForm.value = {
        name: '',
        description: '',
        status: 'draft',
        registration_form_id: '',
        max_participants: null,
        registration_deadline: '',
      }
    }

    const closeModal = () => {
      showCreateModal.value = false
      showEditModal.value = false
      editingCompetition.value = false
      resetForm()
    }

    const editCompetition = (competition) => {
      competitionForm.value = {
        id: competition.id,
        name: competition.name,
        description: competition.description || '',
        status: competition.status || 'draft',
        registration_form_id: competition.registration_form_id,
        max_participants: competition.max_participants,
        registration_deadline: competition.registration_deadline
          ? new Date(competition.registration_deadline)
              .toISOString()
              .slice(0, 10)
          : '',
      }
      editingCompetition.value = true
      showEditModal.value = true
    }

    const saveCompetition = async () => {
      try {
        const endpoint = editingCompetition.value
          ? `/registration/competition/${competitionForm.value.id}`
          : '/registration/competition'

        const method = editingCompetition.value ? 'put' : 'post'

        await axios[method](endpoint, competitionForm.value)

        await fetchCompetitions()
        closeModal()

        const message = editingCompetition.value
          ? 'Competition updated successfully!'
          : 'Competition created successfully!'
        showNotification(message, 'success')
      } catch (error) {
        console.error('Error saving competition:', error)
        showNotification('Failed to save competition', 'error')
      }
    }

    const deleteCompetition = async (competitionId) => {
      if (confirm(t('competitions.deleteCompetitionConfirm'))) {
        try {
          await axios.delete(`/registration/competition/${competitionId}`)
          await fetchCompetitions()
          showNotification('Competition deleted successfully!', 'success')
        } catch (error) {
          console.error('Error deleting competition:', error)
          showNotification('Failed to delete competition', 'error')
        }
      }
    }

    const submissionCount = (competition) => {
      const n = Number(competition?.submission_count)
      return Number.isFinite(n) && n >= 0 ? n : 0
    }

    const viewSubmissions = async (competition) => {
      selectedCompetition.value = competition
      showSubmissionsModal.value = true
    }

    const closeSubmissionsModal = () => {
      showSubmissionsModal.value = false
      selectedCompetition.value = null
    }

    const onSubmissionsDeleted = (count) => {
      if (!selectedCompetition.value || !count) return
      const comp = competitions.value.find(
        (c) => c.id === selectedCompetition.value.id
      )
      if (comp) {
        const current = Number(comp.submission_count || 0)
        comp.submission_count = Math.max(0, current - Number(count))
      }
    }

    const getStatusClass = (competition) => {
      // Use the actual status field from the database
      return competition.status || 'draft'
    }

    const getStatusText = (competition) => {
      // Check registration deadline for additional context
      const now = new Date()
      const deadline = competition.registration_deadline
        ? new Date(competition.registration_deadline)
        : null

      if (competition.status === 'active' && deadline && now > deadline) {
        return t('competitions.registrationClosed')
      }

      return getStatusDisplayText(competition.status)
    }

    const getStatusDisplayText = (status) => {
      const statusMap = {
        draft: t('competitions.statusDraft'),
        active: t('competitions.statusActive'),
        completed: t('competitions.statusCompleted'),
      }
      return statusMap[status] || status
    }

    const formatDate = (dateString) => {
      if (!dateString) return t('general.notSet')
      const date = new Date(dateString)
      return date.toLocaleDateString('et-EE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    }

    // Initialize
    onMounted(() => {
      fetchCompetitions()
      fetchRegistrationForms()
    })

    return {
      competitions,
      registrationForms,
      showCreateModal,
      showEditModal,
      showSubmissionsModal,
      editingCompetition,
      selectedCompetition,
      competitionForm,
      getFormName,
      closeModal,
      editCompetition,
      saveCompetition,
      deleteCompetition,
      submissionCount,
      viewSubmissions,
      closeSubmissionsModal,
      onSubmissionsDeleted,
      getStatusClass,
      getStatusText,
      getStatusDisplayText,
      formatDate,
      showNotificationFlag,
      notificationMessage,
      notificationType,
      t,
    }
  },
}
</script>

<style scoped>
@import './list-styles.css';

.competitions-grid .competitions-table {
  min-width: 800px;
}

/* Competition List Container */
.competition-list-container {
  padding: 1rem;
  background-color: transparent;
  min-height: auto;
}

/* Header */
.header {
  text-align: center;
  margin-bottom: 1.5rem;
  position: relative;
}

.header h2 {
  color: #2c3e50;
  font-size: 2rem;
  margin-bottom: 0.25rem;
}

.subtitle {
  color: #6c757d;
  font-size: 1rem;
  margin-bottom: 1rem;
}

.create-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 2rem 1rem;
  background: white;
  border-radius: 8px;
  border: 1px solid var(--organizer-border, #e7e5e4);
  max-width: 500px;
  margin: 0 auto;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
}

.empty-state h3 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
}

.empty-state p {
  color: #6c757d;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

/* Competitions Table */
/* Using shared list styles via @import */

/* Keep only component-specific deviations below */

.competition-title-cell {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
}

.competition-description-cell {
  color: #6c757d;
  font-size: 0.85rem;
  max-width: 150px;
  line-height: 1.3;
}

.competition-description-cell.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.active {
  background-color: #dcfce7;
  color: #14532d;
}

.status-badge.completed {
  background: #e7e5e4;
  color: #44403c;
}

.status-badge.draft {
  background: #f5f5f4;
  color: #57534e;
  border: 1px solid #e7e5e4;
}

/* Status badge variants are defined in shared styles */

.meta-value {
  color: #495057;
  font-weight: 500;
}

.date-cell {
  color: #6c757d;
  font-size: 0.85rem;
  white-space: nowrap;
}

/* Buttons - Compact Style */
/* Button styles are provided by shared list styles */

.btn-sm {
  padding: 0.2rem 0.4rem;
  font-size: 0.65rem;
}

/* Modal Styling */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  line-height: 1;
  padding: 0;
}

.close-btn:hover {
  color: #495057;
}

.modal-body {
  padding: 1rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

.modal-footer .btn {
  border: none;
  border-radius: 4px;
  padding: 8px 14px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  line-height: 1.2;
  transition: all 0.2s ease;
}

.modal-footer .modal-save-btn {
  background-color: rgba(39, 174, 96, 0.9);
  color: #fff;
  box-shadow: 0 4px 6px rgba(39, 174, 96, 0.3);
}

.modal-footer .modal-save-btn:hover {
  background-color: rgba(46, 204, 113, 0.9);
  box-shadow: 0 6px 8px rgba(39, 174, 96, 0.4);
}

.modal-footer .modal-cancel-btn {
  background-color: rgba(231, 76, 60, 0.9);
  color: #fff;
  box-shadow: 0 4px 6px rgba(231, 76, 60, 0.3);
}

.modal-footer .modal-cancel-btn:hover {
  background-color: rgba(192, 57, 43, 0.9);
  box-shadow: 0 6px 8px rgba(231, 76, 60, 0.4);
}

/* Form Styling */
.form-group {
  margin-bottom: 1rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #495057;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  max-width: 100%;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 0.15rem rgba(52, 152, 219, 0.2);
}

/* Notification */
.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 15px 20px;
  border-radius: 6px;
  color: #fff;
  font-weight: bold;
  z-index: 1001;
  transition: opacity 0.3s ease;
}

.notification.success {
  background-color: #2ecc71;
}

.notification.error {
  background-color: #e74c3c;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
/* Helpers */
.text-right {
  text-align: right;
}

/* Responsive Design */
@media (max-width: 768px) {
  .competition-list-container {
    padding: 0.5rem;
  }
  .competition-description-cell {
    max-width: 120px;
  }
  .form-row {
    grid-template-columns: 1fr;
  }
  .modal-content {
    width: 95%;
    margin: 0.5rem;
  }
}

@media (max-width: 480px) {
  .competition-list-container {
    padding: 0.25rem;
  }
}
</style>
